// Dashboard Hydratation — objectif d'eau quotidien, ajout rapide, stats et courbe.
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { type FormEvent, useMemo, useState } from 'react';
import { WaterChart } from '@/components/charts';
import { SportHydrationNote } from '@/components/SportHydrationNote';
import {
  ChartCard,
  EmptyState,
  ErrorBanner,
  Kpi,
  PeriodPicker,
  ProgressBar,
} from '@/components/ui';
import {
  currentWeight,
  dailyWater,
  dayKey,
  filterByPeriod,
  type Period,
  resolveTargets,
  todayKey,
  waterStats,
} from '@/domain';
import {
  useCreateWater,
  useDeleteWater,
  useExercises,
  useProfile,
  useWater,
  useWeights,
} from '@/hooks/queries';
import { useSubmit } from '@/hooks/useSubmit';
import { confirmDelete } from '@/lib/confirm';
import { useTheme } from '@/theme/ThemeContext';

const QUICK = [
  { ml: 250, label: '🥛 +250' },
  { ml: 500, label: '🍶 +500' },
];

export function HydrationPage() {
  const { data: profile } = useProfile();
  const { data: weights = [] } = useWeights();
  const { data: water = [] } = useWater();
  const { data: exercises = [] } = useExercises();
  const createWater = useCreateWater();
  const deleteWater = useDeleteWater();
  const waterSubmit = useSubmit();
  const { palette } = useTheme();

  const [custom, setCustom] = useState('');
  const [period, setPeriod] = useState<Period>('30d');

  const targetMl = useMemo(() => {
    if (!profile) return 2500;
    return resolveTargets(profile.profile, weights.length ? currentWeight(weights) : NaN).waterMl;
  }, [profile, weights]);

  const stats = useMemo(() => waterStats(water, targetMl), [water, targetMl]);
  const chart = useMemo(
    () => dailyWater(filterByPeriod(water, (w) => w.datetime, period)),
    [water, period],
  );

  const todayEntries = water.filter((w) => dayKey(w.datetime) === todayKey());
  const pct = targetMl > 0 ? Math.round((stats.today / targetMl) * 100) : 0;

  function add(ml: number) {
    if (!Number.isFinite(ml) || ml <= 0) return;
    void waterSubmit.run(() =>
      createWater.mutateAsync({ ml, datetime: new Date().toISOString() }).then(() => undefined),
    );
  }

  function addCustom(e: FormEvent) {
    e.preventDefault();
    const ml = Number(custom);
    if (!custom || !Number.isFinite(ml) || ml <= 0) return;
    void waterSubmit.run(async () => {
      await createWater.mutateAsync({ ml, datetime: new Date().toISOString() });
      setCustom('');
    });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Hydratation</h1>
          <p>
            Objectif quotidien : <strong>{Math.round(targetMl)} ml</strong>
          </p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      <SportHydrationNote exercises={exercises} />

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Aujourd'hui : {Math.round(stats.today)} / {Math.round(targetMl)} ml
          </span>
          <span className="muted" style={{ fontSize: '0.85rem' }}>
            {pct}%
          </span>
        </div>
        <ProgressBar pct={pct} color={pct >= 100 ? palette.aqua : palette.blue} />
        <div className="toolbar" style={{ margin: '0.85rem 0 0' }}>
          {QUICK.map((q) => (
            <button
              type="button"
              key={q.ml}
              onClick={() => add(q.ml)}
              disabled={waterSubmit.pending}
            >
              {q.label} ml
            </button>
          ))}
          <form onSubmit={addCustom} style={{ display: 'flex', gap: '0.4rem' }}>
            <input
              type="number"
              min={0}
              max={5000}
              step={50}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="ml"
              aria-label="Quantité d'eau (ml)"
              style={{ maxWidth: 90 }}
            />
            <button type="submit" className="ghost" disabled={waterSubmit.pending}>
              Ajouter
            </button>
          </form>
        </div>
        {waterSubmit.error && (
          <ErrorBanner message={waterSubmit.error} onRetry={waterSubmit.reset} />
        )}
      </div>

      <div className="grid kpis" style={{ marginBottom: '1rem' }}>
        <Kpi label="Moyenne / jour" value={stats.avgMl} unit="ml" />
        <Kpi
          label="Objectif atteint"
          value={stats.adherencePct}
          unit="%"
          hint={`${stats.daysMet}/${stats.daysLogged} jours`}
        />
        <Kpi
          label="Série en cours"
          value={stats.currentStreak}
          unit="j"
          hint={`record : ${stats.bestStreak} j`}
        />
      </div>

      {water.length === 0 ? (
        <EmptyState>Ajoute ta première prise d'eau pour démarrer le suivi.</EmptyState>
      ) : (
        <>
          <ChartCard
            title="Hydratation par jour"
            subtitle="barres = eau bue (vertes si objectif atteint) · ligne = objectif"
          >
            <WaterChart data={chart} targetMl={targetMl} palette={palette} />
          </ChartCard>

          <div className="card" style={{ marginTop: '1rem' }}>
            <h3>Prises du jour</h3>
            {todayEntries.length === 0 ? (
              <p className="muted" style={{ fontSize: '0.85rem' }}>
                Rien de saisi aujourd'hui.
              </p>
            ) : (
              <div className="table-wrap" style={{ marginTop: '0.5rem' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Heure</th>
                      <th>Quantité</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayEntries.map((w) => (
                      <tr key={w.id}>
                        <td>{format(parseISO(w.datetime), 'HH:mm', { locale: fr })}</td>
                        <td>{w.ml} ml</td>
                        <td>
                          <button
                            type="button"
                            className="danger"
                            onClick={() => {
                              if (confirmDelete('cette prise d’eau')) deleteWater.mutate(w.id);
                            }}
                          >
                            Suppr.
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
